import { AppThunk } from '../../../store/rootReducer';
import { ecgIdSelector } from '../../ecg/selectors/ecg.selectors';
import * as clusterActions from '../actions/cluster.actions';
import * as morphologyActions from '../actions/morphology.actions';
import { fetchClusters, splitParent } from '../client/cluster.client';
import { CLUSTER_CODES } from '../client/helpers';
import { Cluster, ClusterBeat, ServerCluster } from '../clustering.types';

export const reverseMerge =
  (morphology: Cluster): AppThunk =>
  async (dispatch, getState) => {
    const ecgId = ecgIdSelector(getState());

    if (ecgId == null) {
      return null;
    }

    const splitServerClusters = await splitParent(ecgId, morphology.id);
    if (splitServerClusters == null) {
      return;
    }

    const allServerClusters = await fetchClusters(ecgId);
    if (allServerClusters == null) {
      return;
    }

    const clustersByBeatId = splitServerClusters.reduce(
      (acc, cluster) => ({ ...acc, [cluster.id]: cluster }),
      {} as Record<number, Omit<ServerCluster, 'onsets_offsets'>>,
    );

    // we get the onsets_offsets of all split clusters
    const splitClusters = allServerClusters.filter(
      (serverCluster) => clustersByBeatId[serverCluster.id],
    );

    const beatDir: Record<number, ClusterBeat> = morphology.beats.reduce(
      (acc, serverCluster) => ({ ...acc, [serverCluster.id]: serverCluster }),
      {},
    );

    const clusters: Cluster[] = splitClusters
      .filter((splitCluster) => splitCluster.has_children === false)
      .map((splitCluster) => {
        const beats = splitCluster.onsets_offsets.map(
          (onsetOffset) => beatDir[onsetOffset[2]],
        );
        return {
          id: splitCluster.id,
          classification: CLUSTER_CODES[splitCluster.category_code],
          beats,
          priority: 1, // assign random number because priority here
          // is irrelevant the reducer will re-arrange their priorities
        };
      });

    const serverMorphology = splitClusters.find(
      (splitCluster) => splitCluster.has_children,
    );

    const childMorphology: Cluster | null = serverMorphology
      ? {
          id: serverMorphology.id,
          classification: CLUSTER_CODES[serverMorphology.category_code],
          beats: serverMorphology.onsets_offsets.map(
            (onsetOffset) => beatDir[onsetOffset[2]],
          ),
          priority: morphology.priority,
        }
      : null;

    if (childMorphology == null) {
      dispatch(morphologyActions.removeMorphology({ morphology }));
    } else {
      dispatch(
        morphologyActions.replaceMorphology({
          newMorphology: childMorphology,
          oldMorphology: morphology,
        }),
      );
    }
    dispatch(clusterActions.addClusters({ clusters }));
  };
